'use client';

import type { PanInfo } from 'motion/react';
import { useCallback } from 'react';

import { TRACKED_EVENT_POSITION } from '@/constants';
import { useAddClickButtonGtag } from '@/hooks/useGtag.hook';

import { carouselNavLabel } from './constants';

// 이 거리(px) 이상 가로로 끌면 다음/이전 카드로 넘긴다.
const SWIPE_THRESHOLD = 60;

// 모바일 캐러셀의 스와이프(드래그) 입력을 캡슐화. 반환값을 캐러셀 motion.div 에 펼친다.
// (데스크탑의 useRovingCarousel = 키보드 입력, 이쪽 = 스와이프 입력으로 대칭)
export function useSwipe({
  active,
  goTo,
}: {
  active: number;
  goTo: (index: number) => void;
}) {
  // 스와이프 이동도 화살표 버튼과 같은 이벤트·라벨로 추적(method=swipe 로 구분)
  const trackPrev = useAddClickButtonGtag({
    label: carouselNavLabel('prev'),
    position: TRACKED_EVENT_POSITION.MAIN_CONTENT,
  });
  const trackNext = useAddClickButtonGtag({
    label: carouselNavLabel('next'),
    position: TRACKED_EVENT_POSITION.MAIN_CONTENT,
  });

  const onDragEnd = useCallback(
    (_: MouseEvent | TouchEvent | PointerEvent, info: PanInfo) => {
      if (info.offset.x < -SWIPE_THRESHOLD) {
        trackNext(undefined, { method: 'swipe' });
        goTo(active + 1);
      } else if (info.offset.x > SWIPE_THRESHOLD) {
        trackPrev(undefined, { method: 'swipe' });
        goTo(active - 1);
      }
    },
    [active, goTo, trackPrev, trackNext],
  );

  return {
    drag: 'x' as const,
    dragConstraints: { left: 0, right: 0 },
    dragElastic: 0.15,
    dragSnapToOrigin: true,
    onDragEnd,
  };
}
